import { AxiosError } from "axios";
import ajaxClient from "@/common/AjaxClient";

interface ReportedError {
    message: string;
    fileName: string;
    lineNumber: number;
    columnNumber: number;
    referrer: string;
    stack?: string | null;
    errorObject?: string;
    content?: string;
}

interface ErrorEventWithStack extends ErrorEvent {
    error: { stack: string };
}

const unreportableErrors: RegExp[] = [
    /^(Uncaught )?Error: Page config not found for this page\.$/,
    /^Network Error$/,
    /^(Uncaught )?Error: Could not find config JSON info at ([\w-#.]+)\.$/,
    /^timeout exceeded$/,
    /^The operation was aborted\.$/,
    /^Request failed with status code (403|500|502|520|524)$/,
    /^(Uncaught )?Error: No shown image$/,
    /^(Uncaught )?Error: Session start timestamp not defined\.$/,
    /^Failed to execute 'pushState' on 'History':/,
    /^The object can not be cloned\.$/,
    /^Function object could not be cloned\.$/,
    /^(Uncaught NotFoundError: )?Failed to execute '(removeChild|insertBefore)' on 'Node':/,
    /^chain is not set up$/,
    /^The play method is not allowed by the user agent or the platform in the current context, possibly because the user denied permission\.$/,
    /^The media resource indicated by the src attribute or assigned media provider object was not suitable.$/,
    /^NetworkError when attempting to fetch resource\.$/,
    /^sharedStorage is disabled$/,
    /^(Uncaught )?SyntaxError: Function statements require a function name$/,
];

const unreportableFiles: RegExp[] = [
    /^https:\/\/static\.cloudflareinsights\.com/,
    /^https:\/\/fpnpmcdn\.net/,
];

const unreportableStackContents: RegExp[] = [
    /connect.facebook.net/,
    /chrome-extension:\/\//,
    /@webkit-masked-url/,
    /sharedStorage is disabled/,
];

const isUnreportableError = (message: string): boolean => unreportableErrors.some((unreportableError: RegExp) => unreportableError.test(message));
const isUnreportableFile = (fileName: string): boolean => unreportableFiles.some((unreportableFile: RegExp) => unreportableFile.test(fileName));
const isUnreportableStackContent = (stack: string): boolean => unreportableStackContents.some((unreportableStackContent: RegExp) => unreportableStackContent.test(stack));

// eslint-disable-next-line complexity
const reportError = (event: ErrorEventWithStack, options?: ErrorReportingOptions): void => {
    try {
        if (isUnreportableError(event.message)) {
            return;
        }

        if (isUnreportableFile(event.filename)) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (event.error?.stack && isUnreportableStackContent(event.error.stack)) {
            return;
        }

        const error: ReportedError = {
            message: event.message,
            fileName: event.filename,
            lineNumber: event.lineno,
            columnNumber: event.colno,
            referrer: globalThis.location.href,
        };

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (event.error?.stack) {
            error.stack = event.error.stack;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/strict-boolean-expressions
        if (event.error) {
            error.errorObject = createErrorObjectMessage(event.error as Error);
        }

        void ajaxClient.post(
            "/Service/jsLogJSError",
            {
                error: error,
                content: ((options?.sendContent) === true) ? document.documentElement.outerHTML : undefined,
            },
        );
    } catch {
        // Empty
    }
};

const createErrorObjectMessage = (error: AxiosError | Error): string => {
    try {
        if (error instanceof AxiosError) {
            return JSON.stringify(error.toJSON());
        }
    } catch {
        return JSON.stringify(error);
    }

    try {
        return JSON.stringify(error);
    } catch {
        return `String: ${error.toString()}`;
    }
};

const reportUnhandledRejection = (event: PromiseRejectionEvent, options?: ErrorReportingOptions): void => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (event.reason === undefined || event.reason.message === undefined) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        if (isUnreportableError(event.reason.message.toString())) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-call
        if (event.reason.stack !== undefined && isUnreportableStackContent(event.reason.stack.toString())) {
            return;
        }

        void ajaxClient.post("/Service/jsLogJSError", {
            error: {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
                message: event.reason.message.toString(),
                fileName: "unhandledrejection.js",
                lineNumber: 0,
                columnNumber: 0,
                referrer: globalThis.location.href,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
                stack: event.reason.stack.toString(),
                errorObject: createErrorObjectMessage(event.reason as Error),
            },
            content: ((options?.sendContent) === true) ? document.documentElement.outerHTML : undefined,
        });
    } catch {
        // Empty
    }
};

interface ErrorReportingOptions {
    readonly sendContent?: boolean;
}

export default function initJSErrorReporting(options?: ErrorReportingOptions): void {
    globalThis.addEventListener("error", (event: ErrorEvent) => reportError(event, options));
    globalThis.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => reportUnhandledRejection(event, options));
}
